import { log } from '@/log'
import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import { SessionCookie, SessionCookiePayload } from '@/services/session/session-cookie'

const MAX_KEEP_RETRY_COUNT = 3

/**
 * シングルトンでセッションの初期化状態を管理する
 */
export class SessionService {
  static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService()
    }

    return SessionService.instance
  }

  private static instance: SessionService

  private initPromise: Promise<SessionCookiePayload> | null = null
  private keepRetryCount: number = 0

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionCookie: SessionCookie = new SessionCookie()
  ) {}

  init(): Promise<SessionCookiePayload> {
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = this.innerInit().then(
      (payload: SessionCookiePayload) => {
        log.debug({ service: 'session/init', payload })

        return payload
      }
    )

    return this.initPromise
  }

  /**
   * FIXME:
   * この実装だと前のkeepが完了しない間に次のkeepが実行されることがあるため、
   * 厳密性の観点ではpromiseをキューに入れて逐次実行するような実装が望ましい
   * @see https://github.com/Everforth/growthpalette-front/pull/8742#discussion_r2578394375
   */
  async keep(viewedPath: string): Promise<void> {
    const retry = async (errorCode: string) => {
      this.keepRetryCount += 1
      if (this.keepRetryCount > MAX_KEEP_RETRY_COUNT) {
        log.error({
          service: 'session/keep',
          message: 'Max retry count exceeded for keep operation',
          errorCode,
          retryCount: this.keepRetryCount,
        })

        return
      }

      this.sessionCookie.clear()
      this.resetInitPromise().keep(viewedPath)
    }
    const handleError = async (errorCode: string) => {
      // クッキーが不正の場合は初期化をリトライする
      if (errorCode === 'invalid_cookie' || errorCode === 'not_found') {
        return await retry(errorCode)
      }

      // リトライカウントをリセット
      this.keepRetryCount = 0

      // 入店制限によって入店拒否されれば /entrance/* にリダイレクト
      if (errorCode === 'entry_rejected') {
        const currentPath = location.pathname.replace(/^\//, '')
        const currentQuery = location.search

        location.href = `/entrance/${currentPath}${currentQuery}`

        return
      }
    }

    // initの完了を待ってからkeepを行う
    this.init().then(async () => {
      const keepResult = await this.sessionApiService.keepCurrent(viewedPath)
      keepResult.fold(
        (error: SessionApiErrorResponse) => {
          handleError(error.errorCode)
        },
        () => {
          this.keepRetryCount = 0
        }
      )
    })
  }

  private resetInitPromise(): SessionService {
    this.initPromise = null

    return this
  }

  private async innerInit(): Promise<SessionCookiePayload> {
    const maybePayload = this.sessionCookie.readPayload()

    // クッキーが存在すれば初期化済みとする
    if (maybePayload) {
      return maybePayload
    }

    // クッキーが存在しない場合はnew
    return await this.new()
  }

  private async new(): Promise<SessionCookiePayload> {
    const isHlApp = HlAppMode.isPageForHlApp || HlAppMode.isViewedInHlAppWebView
    const result = await this.sessionApiService.new({
      isHlApp,
    })

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      () => {
        const payload = this.sessionCookie.readPayload()
        if (!payload) {
          throw new Error('Failed to read session cookie payload')
        }

        return payload
      }
    )
  }
}
